import { type Card, type Suit } from '../../engine/card.js';
import type { GameState, Pile } from '../../engine/game-state.js';
import type { Move } from '../../engine/moves.js';
import type { SolitaireRules } from '../../engine/rules.js';

// 10 tableau columns, 8 foundations (each holds one completed K→A same-suit
// run), 1 stock pile that deals 10 cards at a time face-up across the row.
export const TABLEAU_IDS = [
  't1', 't2', 't3', 't4', 't5', 't6', 't7', 't8', 't9', 't10',
] as const;
export const FOUNDATION_IDS = [
  'f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8',
] as const;

export type SpiderSuitCount = 1 | 2 | 4;

function emptyPile(id: string, type: Pile['type']): Pile {
  return { id, type, cards: [] };
}

/**
 * Build a 104-card deck constrained to `suits` distinct suits. Spider's
 * difficulty knob:
 *   • 1 suit  → 8 copies of one suit (easiest - every card of one rank pairs)
 *   • 2 suits → 4 copies of each of two suits
 *   • 4 suits → 2 copies of every suit (full game, hardest)
 * Card ids are guaranteed unique by appending the copy index.
 */
export function buildSpiderDeck(suits: SpiderSuitCount): Card[] {
  const useSuits: Suit[] =
    suits === 1 ? ['spades']
    : suits === 2 ? ['spades', 'hearts']
    : ['spades', 'hearts', 'clubs', 'diamonds'];
  const copiesPerSuit = 104 / 13 / suits;
  const deck: Card[] = [];
  let idx = 0;
  for (let copy = 0; copy < copiesPerSuit; copy++) {
    for (const suit of useSuits) {
      for (let rank = 1; rank <= 13; rank++) {
        deck.push({
          id: `${suit[0]}${rank}_${idx++}`,
          suit,
          rank: rank as Card['rank'],
          faceUp: false,
        });
      }
    }
  }
  return deck;
}

interface SpiderOptions {
  suits?: SpiderSuitCount;
}

/**
 * A run is "movable" when its cards are face-up, same-suit, and descending
 * by exactly 1. The lead is the topmost card visually (highest index in the
 * pile, lowest rank in the run).
 *
 * NB: this matches the *single* same-suit descending run starting at the
 * given index. In 4-suit Spider you can rest a 6♥ on a 7♠ during play, but
 * you can't drag the pair as a unit - `dragStackIds` will refuse.
 */
export function isSameSuitDescendingRun(cards: Card[]): boolean {
  if (cards.length === 0) return false;
  for (const c of cards) if (!c.faceUp) return false;
  const suit = cards[0]!.suit;
  for (let i = 1; i < cards.length; i++) {
    if (cards[i]!.suit !== suit) return false;
    if (cards[i]!.rank !== cards[i - 1]!.rank - 1) return false;
  }
  return true;
}

/** Index of the start of a complete K→A same-suit run sitting at the bottom
 *  of the pile (i.e. ready to graduate to a foundation), or -1 if none. */
function findCompleteRun(pile: Pile): number {
  if (pile.cards.length < 13) return -1;
  const start = pile.cards.length - 13;
  const lead = pile.cards[start]!;
  if (!lead.faceUp || lead.rank !== 13) return -1;
  for (let i = 0; i < 13; i++) {
    const c = pile.cards[start + i]!;
    if (c.suit !== lead.suit || c.rank !== 13 - i) return -1;
  }
  return start;
}

function findEmptyFoundation(state: GameState): string | null {
  for (const id of FOUNDATION_IDS) {
    if ((state.piles[id]?.cards.length ?? 0) === 0) return id;
  }
  return null;
}

function setupSpider(suits: SpiderSuitCount): GameState {
  const piles: Record<string, Pile> = {};
  for (const id of TABLEAU_IDS) piles[id] = emptyPile(id, 'tableau');
  for (const id of FOUNDATION_IDS) piles[id] = emptyPile(id, 'foundation');
  piles.stock = emptyPile('stock', 'stock');
  return { piles, movesCount: 0 };
}

/**
 * `setup(deck)` ignores the input deck composition and rebuilds it from the
 * configured suit count. The shuffled order from the input *is* honored -
 * `setup` consumes the cards in the order it generates them, which respects
 * `shuffle(buildSpiderDeck(...))` upstream.
 */
export function makeSpiderRules(opts: SpiderOptions = {}): SolitaireRules {
  const suits: SpiderSuitCount = opts.suits ?? 4;

  return {
    id: `spider${suits === 4 ? '' : `-${suits}`}`,

    setup(deck: Card[]) {
      // If the caller already gave us a 104-card spider-shaped deck, use its
      // order. Otherwise rebuild (test code defaults to createDeck(2) which
      // gives a 4-suit double-deck - fine for default 4-suit Spider).
      const cards = deck.length === 104 ? deck.slice() : buildSpiderDeck(suits);
      const state = setupSpider(suits);
      // First 4 columns get 6 cards (5 face-down + 1 face-up), remaining 6
      // columns get 5 cards (4 face-down + 1 face-up). Total dealt: 54.
      let i = 0;
      for (let col = 0; col < 10; col++) {
        const count = col < 4 ? 6 : 5;
        const pile = state.piles[TABLEAU_IDS[col]!]!;
        for (let n = 0; n < count; n++) {
          const c = cards[i++]!;
          c.faceUp = n === count - 1; // top card face-up
          pile.cards.push(c);
        }
      }
      // Remaining 50 cards face-down to stock.
      for (; i < cards.length; i++) {
        const c = cards[i]!;
        c.faceUp = false;
        state.piles.stock!.cards.push(c);
      }
      return state;
    },

    canMove(state, move: Move) {
      const from = state.piles[move.from];
      const to = state.piles[move.to];
      if (!from || !to || from === to) return false;

      // Stock dealing happens in the spec (one click → one card per column),
      // not through canMove - Move can only describe a single from/to pair.
      if (from.type === 'stock') return false;

      // Tableau → foundation: only valid via afterMove auto-graduation. Reject
      // user-initiated foundation drops to keep the rule surface tight.
      if (to.type === 'foundation') return false;

      if (from.type !== 'tableau' || to.type !== 'tableau') return false;

      const idx = from.cards.findIndex((c) => c.id === move.cardId);
      if (idx === -1) return false;

      const run = from.cards.slice(idx);
      if (!isSameSuitDescendingRun(run)) return false;

      // Empty target accepts any run.
      if (to.cards.length === 0) return true;

      // Otherwise destination top must be the run lead's rank + 1 (any suit).
      const lead = run[0]!;
      const dest = to.cards[to.cards.length - 1]!;
      return dest.faceUp && dest.rank === lead.rank + 1;
    },

    afterMove(state) {
      // 1. Any newly exposed top card flips face-up.
      flipTops(state);

      // 2. Auto-graduate any complete K→A same-suit run sitting at the bottom
      //    of a tableau to the next empty foundation. This may chain (the
      //    flip in step 1 might enable new graduations on the next pass).
      let changed = true;
      while (changed) {
        changed = false;
        for (const id of TABLEAU_IDS) {
          const pile = state.piles[id]!;
          const start = findCompleteRun(pile);
          if (start === -1) continue;
          const foundationId = findEmptyFoundation(state);
          if (!foundationId) continue;
          const run = pile.cards.splice(start);
          state.piles[foundationId]!.cards.push(...run);
          flipTops(state);
          changed = true;
        }
      }
    },

    isWon(state) {
      // All tableau columns empty ⇒ all 104 cards graduated to foundations.
      for (const id of TABLEAU_IDS) {
        if ((state.piles[id]?.cards.length ?? 0) > 0) return false;
      }
      return true;
    },
  };
}

function flipTops(state: GameState) {
  for (const id of TABLEAU_IDS) {
    const pile = state.piles[id];
    if (!pile) continue;
    const top = pile.cards[pile.cards.length - 1];
    if (top && !top.faceUp) top.faceUp = true;
  }
}

export const spiderRules: SolitaireRules = makeSpiderRules({ suits: 4 });

/**
 * Stock deal: pop one card per tableau column, face-up. Spider rule: forbidden
 * while *any* tableau column is empty (forces the player to fill empties from
 * the existing tableau before getting fresh cards).
 */
export function dealRow(state: GameState): boolean {
  const stock = state.piles.stock!;
  if (stock.cards.length === 0) return false;
  if (stock.cards.length < TABLEAU_IDS.length) return false;
  for (const id of TABLEAU_IDS) {
    if (state.piles[id]!.cards.length === 0) return false;
  }
  for (const id of TABLEAU_IDS) {
    const c = stock.cards.pop()!;
    c.faceUp = true;
    state.piles[id]!.cards.push(c);
  }
  return true;
}
